import { useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';

import { Loading } from '#components/loading.js';
import { ButtonLink } from '#components/router-links/button-link.js';
import { useTRPC } from '#src/trpc.js';
import { useDataElTagGenerator } from '#utils/use-data-el-tag-generator.js';

export const Route = createFileRoute('/_authorization-gate/wizard/')({
	staticData: {
		authorize: (ability) => ability.can('manage', 'wizard'),
		navigation: {
			label: 'Wizard',
		},
	},
	head: () => ({ meta: [{ title: 'Wizard • Cadstrom' }] }),
	component: WizardHomePage,
	pendingComponent: Loading,
});

function WizardHomePage() {
	const trpc = useTRPC();

	// Temporary while plan == package: the "tasks available" copy below counts
	// plans, not tasks. When a plan represents a broader collection of tasks
	// than a single package's understanding, swap this for a per-plan task
	// projection (or a dedicated task-count endpoint) and update the copy.
	const { data: planCount } = useSuspenseQuery(
		trpc.wizard.step.getAvailablePlanCount.queryOptions(
			{},
			{
				select: (data) => {
					if (!data.isSuccess) {
						throw new Error(data.errorMessage);
					}
					return data.count;
				},
				// The queue is drained by every other operator too, so a count cached from an
				// earlier visit is a promise this page cannot keep. Re-read it whenever the page
				// is opened or refocused, rather than offering work that has since been claimed.
				staleTime: 0,
				refetchOnMount: 'always',
				refetchOnWindowFocus: true,
			},
		),
	);

	const el = useDataElTagGenerator(WizardHomePage);
	return (
		<div {...el()} className='grid grow place-items-center p-4'>
			<div {...el('content')} className='flex flex-col items-start gap-4'>
				<div className='flex flex-col gap-2'>
					<p className='text-xl font-semibold text-gray-a12'>Welcome!</p>
					<p className='text-gray-a11'>
						There {planCount === 1 ? 'is' : 'are'} currently{' '}
						<span className='font-bold text-gray-a12'>{planCount}</span> task{planCount === 1 ? '' : 's'} available to
						work on.
					</p>
				</div>

				<div className='flex self-stretch'>
					<ButtonLink to='/wizard/go' disabled={planCount === 0} className='basis-24'>
						<span>Begin</span>
					</ButtonLink>
				</div>
			</div>
		</div>
	);
}
